<template>
  <div class="emailing-view flex h-[calc(100vh-80px)]">
    <!-- Détecteur de changement de marque -->
    <BrandChangeDetector :support-id="route.params.id" />

    <!-- Overlay de chargement -->
    <div v-if="isSaving" class="pdf-loading-overlay">
      <div class="pdf-loading-content loader-standard">
        <div class="spinner" />
        <div class="loading-text">
          Enregistrement en cours…
        </div>
      </div>
    </div>

    <!-- Panneau latéral -->
    <ProductsPanel />

    <!-- Zone principale -->
    <div class="flex-1 bg-gray-100 p-8 overflow-auto">
      <div class="max-w-5xl mx-auto">
        <!-- En-tête -->
        <div class="mb-8 flex items-center justify-between">
          <h1 class="text-2xl font-bold text-gray-900">
            {{
              isNewSupport
                ? "Création d'un emailling"
                : "Édition d'un emailling"
            }}
          </h1>
          <div class="flex items-center gap-4">
            <button
              class="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700"
              @click="generateAndDownloadHTML"
            >
              📧 Générer HTML
            </button>
            <button
              class="px-4 py-2 bg-brand text-white rounded-md shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              :disabled="isSaving"
              @click="handleSaveAndGeneratePDF"
            >
              <i v-if="isSaving" class="fas fa-spinner fa-spin" />
              <span>{{ isSaving ? "Enregistrement..." : "Enregistrer" }}</span>
            </button>
          </div>
        </div>

        <!-- Configuration -->
        <!-- Canvas -->
        <SupportCanvaEmail
          v-if="supportStore.currentSupport"
          ref="supportCanvasRef"
        />
        <div v-else class="flex items-center justify-center h-64">
          <p class="text-gray-500">
            Chargement du support...
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SupportCanvaEmail from "@/components/supports/email/SupportCanvaEmail.vue";
import ProductsPanel from "@/components/supports/panels/ProductsPanel.vue";
import BrandChangeDetector from "@/components/supports/BrandChangeDetector.vue";
import { useSupportStore } from "@/stores/supports/useSupportStore";
import { useHtmlDownload } from "@/composables/supports/useHtmlDownload";
import { onMounted, ref, watch, computed } from "@vue/runtime-core";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";

const route = useRoute();
const router = useRouter();
const toast = useToast();
const successSound = new Audio("/sounds/success.wav");
const errorSound = new Audio("/sounds/error.mp3");

const playSound = (sound: HTMLAudioElement) => {
  sound.currentTime = 0; // Réinitialiser le son
  sound.play().catch((error) => {
    console.warn("Erreur lors de la lecture du son:", error);
  });

  // Couper le son après 200ms pour le son iPhone
  setTimeout(() => {
    sound.pause();
    sound.currentTime = 0;
  }, 400);
};

// Ajuster le volume des sons
successSound.volume = 0.3; // 30% du volume pour le son iPhone
errorSound.volume = 0.15; // 15% du volume pour l'erreur

const supportStore = useSupportStore();
const format = ref("email");
const template = ref("default");
const supportCanvasRef = ref();
const { checkForHtmlDownload } = useHtmlDownload(supportCanvasRef);
const isSaving = ref(false);
const isNewSupport = computed(
  () => !route.params.id || route.params.id === "nouveau"
);

// Fonction pour initialiser le support
const initializeSupport = async () => {
  supportStore.resetStore();

  if (isNewSupport.value) {
    await supportStore.initSupport("emailing");
  } else {
    try {
      await supportStore.loadSupport("emailing", route.params.id as string);
    } catch (error) {
      console.error("Erreur lors du chargement du support:", error);
    }
  }
};

watch(
  () => route.fullPath,
  () => {
    initializeSupport();
  }
);

onMounted(() => {
  initializeSupport();
  // Vérifier si on doit télécharger le HTML automatiquement
  checkForHtmlDownload();
});

// Mettre à jour le support quand le format ou le template change
watch([format, template], () => {
  supportStore.updateSupport({
    format: format.value,
    template: template.value,
  });
});

// Fonction factorisée pour générer le PDF à partir du composant affiché
const generateAndGetPdfBlob = async (): Promise<Blob> => {
  // Synchronisation du store avec les données du composant
  if (
    supportCanvasRef.value &&
    typeof supportCanvasRef.value.getCurrentState === "function"
  ) {
    const currentState = supportCanvasRef.value.getCurrentState();
    if (currentState) {
      console.log("État actuel récupéré:", currentState);
      // Met à jour le store avec l'état courant du composant (pages, produits, pictos, etc.)
      supportStore.updateSupport(currentState);
    }
  }
  let previewWasActivated = false;
  if (!supportStore.isPreview) {
    supportStore.togglePreview();
    previewWasActivated = true;
    await new Promise((resolve) => setTimeout(resolve, 500));
  }
  const pdfBlob = await supportCanvasRef.value.generatePdfBlob();
  if (previewWasActivated) {
    supportStore.togglePreview();
  }
  return pdfBlob;
};

// Méthodes de sauvegarde et export
const handleSaveAndGeneratePDF = async () => {
  // Timeout de sécurité pour éviter un spinner bloqué
  const timeoutId = setTimeout(() => {
    if (isSaving.value) {
      console.warn("Timeout de sauvegarde atteint, arrêt du spinner");
      isSaving.value = false;
      toast.error("La sauvegarde a pris trop de temps. Veuillez réessayer.");
    }
  }, 120000); // 2 minutes

  try {
    console.log("=== DÉBUT SAUVEGARDE EMAIL ===");
    isSaving.value = true;

    // 1. Synchroniser l'état du composant avec le store AVANT la génération
    if (
      supportCanvasRef.value &&
      typeof supportCanvasRef.value.getCurrentState === "function"
    ) {
      const currentState = supportCanvasRef.value.getCurrentState();
      if (currentState) {
        console.log("Synchronisation avant sauvegarde:", currentState);
        supportStore.updateSupport(currentState);
      }
    }

    // 2. Générer le HTML AVANT de sauvegarder le support
    console.log("Génération du HTML...");
    const htmlContent = await supportCanvasRef.value.generateHtmlEmail();
    console.log("HTML généré avec succès, longueur:", htmlContent.length);

    // 4. Sauvegarder le support (le PDF est généré côté serveur).
    console.log("Sauvegarde du support...");
    const response = await supportStore.saveSupport();
    if (response?.data?.id) {
      supportStore.currentSupport.id = response.data.id;
      console.log("Support sauvegardé avec ID:", response.data.id);
    }

    // 5. Création du FormData pour le HTML
    console.log("Envoi du HTML au backend...");
    const htmlFormData = new FormData();
    const htmlFile = new File([htmlContent], "support.html", {
      type: "text/html;charset=utf-8",
    });
    htmlFormData.append("html", htmlFile);
    htmlFormData.append("supportId", supportStore.currentSupport.id.toString());
    htmlFormData.append("brandSlug", localStorage.getItem("brand") || "hv");
    htmlFormData.append(
      "supportType",
      supportStore.currentSupport.type || supportStore.supportType
    );

    // 6. Envoi du HTML au backend
    await supportStore.sendHtmlToBackend(htmlFormData);
    console.log("HTML envoyé au backend avec succès");

    // 7. FormData PDF (sans PDF : serveur génère le vectoriel ; capture en fallback)
    const pdfFormData = new FormData();
    pdfFormData.append("supportId", supportStore.currentSupport.id.toString());
    pdfFormData.append("brandSlug", localStorage.getItem("brand") || "hv");
    pdfFormData.append(
      "supportType",
      supportStore.currentSupport.type || supportStore.supportType
    );

    // 8. Persistance du PDF (vectoriel async + notification ; capture en fallback)
    await supportStore.persistSupportPdf(
      pdfFormData,
      () => generateAndGetPdfBlob(),
      // Emailing : capture client (screenshot) — meilleur rendu que le vectoriel.
      undefined,
      true
    );

    playSound(successSound);
    toast.success(response.data.message || "Support sauvegardé avec succès");
    router.push("/documents");
  } catch (error) {
    console.error(
      "Erreur lors de la sauvegarde et génération du HTML/PDF:",
      error
    );
    const errorMessage = error instanceof Error ? error.message : String(error);
    toast.error(
      `Une erreur est survenue lors de la sauvegarde du support: ${errorMessage}`
    );
    // Ne pas re-throw l'erreur, juste la logger
  } finally {
    clearTimeout(timeoutId);
    isSaving.value = false;
  }
};

const generateAndDownloadHTML = async () => {
  try {
    const htmlContent = await supportCanvasRef.value.generateHtmlEmail();

    // Créer un blob avec le HTML généré
    const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    // Créer un lien de téléchargement
    const link = document.createElement("a");
    link.href = url;
    link.download = `email-${supportStore.currentSupport?.name || "agco"}-${
      new Date().toISOString().split("T")[0]
    }.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Nettoyer l'URL
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);

    toast.success("HTML généré et téléchargé avec succès");
  } catch (error) {
    console.error("Erreur lors de la génération du HTML:", error);
    toast.error("Erreur lors de la génération du HTML");
  }
};
</script>

<script lang="ts">
export default {
  name: "EmailingView",
};
</script>

<style scoped>
/* Styles pour l'impression et l'aperçu */
@media print {
  .support-preview {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
  }
}

/* Styles pour l'overlay de chargement */
.pdf-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.pdf-loading-content {
  text-align: center;
}

.loader-standard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 56px;
  height: 56px;
  border: 6px solid #e5e7eb;
  border-top: 6px solid #b70018;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.25rem;
  color: #2d3a3a;
  font-weight: 600;
  letter-spacing: 0.02em;
}
</style>
